import { NextSeo } from 'next-seo';
import { memo } from 'react';

import { type Board } from '@air/api/types';
import { buildClientImgixUrl } from '@air/component-imgix-image';
import { useShortIdContext } from '@air/provider-short-id';

import { usePublicWorkspace } from '~/components/PublicWorkspaceProvider';
import { createShortUrlBoardRoute } from '~/constants/routes';
import { getSeoTitle, getShareSeoDescription } from '~/swr-hooks/shortUrl/utils';
import { getPageUrl } from '~/utils/Url';

type PublicBoardSEOProps = {
  board: Required<Pick<Board, 'id' | 'title' | 'description' | 'thumbnails'>>;
};

export const PublicBoardSEO = memo(({ board }: PublicBoardSEOProps) => {
  const { workspaceName } = usePublicWorkspace();
  const { shortId } = useShortIdContext();
  const origin = getPageUrl();
  const url = `${origin}${createShortUrlBoardRoute(shortId, board.id)}`;
  const thumbnail = board.thumbnails[0];

  const ogImage = thumbnail
    ? buildClientImgixUrl(thumbnail, { w: 1200, h: 1200, fit: 'clip', auto: 'format' })
    : undefined;
  const ogImageUrl = ogImage?.startsWith('/') ? `${origin}${ogImage}` : ogImage;

  return (
    <NextSeo
      nofollow
      noindex
      title={getSeoTitle('Board', board.title, workspaceName)}
      description={getShareSeoDescription(board.description)}
      canonical={url}
      openGraph={{ url, images: ogImageUrl ? [{ url: ogImageUrl, alt: '' }] : undefined }}
    />
  );
});

PublicBoardSEO.displayName = 'PublicBoardSEO';
